// Work-value maths (TD-078), ported from backend/domains/finance/pricing/work_values.py: a split's total and the
// largest-remainder split of a price by weights, in integers only, so the sides agree to the cent.
import type { WorkValues } from "../types";

export const workTotal = (values: WorkValues): number => Object.values(values).reduce((s, v) => s + v, 0);

const zeros = (weights: WorkValues): WorkValues => Object.fromEntries(Object.keys(weights).map((k) => [k, 0]));

/** Python's `a // b` for integers and b > 0, exact (no float division). */
const floorDiv = (a: number, b: number): number => (a - (((a % b) + b) % b)) / b;

/**
 * Largest-remainder split of `total` by `weights`, in integers only. The spare units go one each
 * to the keys sorted by (-remainder, -weight, key). The result does not depend on key order.
 */
export function lrSplit(total: number, weights: WorkValues): WorkValues {
  const keys = Object.keys(weights);
  const sum = workTotal(weights);
  if (sum === 0) return zeros(weights);
  const out: WorkValues = {};
  const rem: WorkValues = {};
  keys.forEach((k) => {
    const product = total * weights[k];
    out[k] = floorDiv(product, sum);
    rem[k] = product - out[k] * sum;
  });
  let left = total - workTotal(out);
  const order = keys.slice().sort((a, b) => {
    if (rem[a] !== rem[b]) return rem[b] - rem[a];
    if (weights[a] !== weights[b]) return weights[b] - weights[a];
    return a < b ? -1 : a > b ? 1 : 0;
  });
  order.forEach((k) => {
    if (left <= 0) return;
    out[k] += 1;
    left -= 1;
  });
  return Object.fromEntries(keys.map((k) => [k, out[k]]));
}
